/**
 * All assigned courses list.
 */
import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Box, Tabs, Tab, Grid, TextField } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import { useAsterController } from "context";
import { BCourseGridItem, BCourseListItem, BPopOver } from "components";
import BZoomGridItem from "components/BZoomGridItem";
import BZoomListItem from "components/BZoomListItem";

/**
 * Grid/list of every course assigned to the learner.
 */
const AssignedAll = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { mobile, courses, meetEvents, loadedDashboard } = controller;

  const [tab, setTab] = useState(0);
  const [contents, setContents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [term, setTerm] = useState("");

  useEffect(() => mobile && setTab(0), [mobile]);
  useEffect(() => {
    if ((courses.data || []).length) {
      const tmp = term.toLowerCase();
      setContents((courses.data || []).filter((course) => course.enrollment_status).filter((v) => v.display_name?.toLowerCase()?.includes(tmp)));
    }
  }, [courses.data, term]);

  useEffect(() => {
    const enrolledCourseIds = contents.map((v) => v.id);
    setSessions((meetEvents.data || []).filter((session) => session.courses.map((v) => v.id).some((id) => enrolledCourseIds.includes(id))));
  }, [contents.length, contents, meetEvents.data, term]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  return (
    <>
      <Box className="md:px-14 px-4 py-8">
        <Box className="pb-6 flex" id="v-user-title">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} className="w-full md:flex hidden justify-center md:justify-start">
              <VText className="text-2xl">{(contents.length || 0) + (sessions.length || 0)}</VText>&nbsp;
              <VText color="secondary" className="text-2xl">
                {t("Contents in total")}
              </VText>
            </Grid>
            <Grid item xs={12} md={8} className="flex justify-end flex-wrap items-center">
              <VButton variant="text" color="secondary" className="text-sm normal-case" onClick={() => setTerm("")} disabled={!term}>
                <Icon icon="akar-icons:circle-check" />
                &nbsp;{t("All status")}
              </VButton>
              <BPopOver
                className="ml-2"
                autoClose={false}
                trigger={
                  <VButton variant="text" color="secondary" className="text-sm normal-case">
                    <Icon icon="ant-design:filter-filled" />
                    &nbsp;{t("Filter")}
                  </VButton>
                }
              >
                <TextField className="w-64" size="small" value={term} onChange={(e) => setTerm(e.target.value)} />
              </BPopOver>
              <Box className="md:flex hidden h-8 ml-2 v-tab-switch">
                <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue}>
                  <Tab icon={<Icon className="mr-0" icon="akar-icons:grid" />} />
                  <Tab icon={<Icon className="mr-0" icon="ci:list-checklist" />} />
                </Tabs>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {tab === 0 && (
          <Box>
            <Grid container spacing={3}>
              {loadedDashboard ? (
                <>
                  {contents.map((course) => (
                    <Grid key={course.course_index} item xs={12} sm={6} md={4} xl={3}>
                      <BCourseGridItem course={course} />
                    </Grid>
                  ))}
                  {sessions.map((session) => (
                    <Grid key={session.id} item xs={12} sm={6} md={4} xl={3}>
                      <BZoomGridItem session={session} />
                    </Grid>
                  ))}
                </>
              ) : (
                [...Array(10).keys()].map((v) => (
                  <Grid key={v} item xs={12} sm={6} md={4} xl={3}>
                    <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 p-4">
                      <Skeleton className="aspect-[9/5] mb-4" />
                      <Skeleton count={4} className="mt-2" />
                      <Skeleton className="h-8 mt-4" />
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}
        {tab === 1 && (
          <Box>
            <Grid container spacing={3}>
              {loadedDashboard ? (
                <>
                  {contents.map((course) => (
                    <Grid key={course.course_index} item xs={12}>
                      <BCourseListItem course={course} />
                    </Grid>
                  ))}
                  {sessions.map((session) => (
                    <Grid key={session.id} item xs={12}>
                      <BZoomListItem session={session} />
                    </Grid>
                  ))}
                </>
              ) : (
                [...Array(10).keys()].map((v) => (
                  <Grid key={v} item xs={12}>
                    <Skeleton className="w-full h-32" />
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AssignedAll;
